import { ReactComponent as CalendarIcon } from "assets/icons/calendar.svg";
import { ReactComponent as TrashRedIcon } from "assets/icons/trash-red.svg";
import { getDay, isSameDay } from "date-fns";
import format from "date-fns/format";
import React, { useContext, PropsWithChildren } from "react";
import { BeforeAfterModifier, DayModifiers } from "react-day-picker";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { Button, IInput, Input } from "..";
import "../../../../css/datePicker.component.css";
import { DropdownIndicator } from "../Dropdown/DropdownIndicator";
import "./../DatePicker/DatePicker.css";
import { Navbar } from "./../DatePicker/Navbar";
import { AccessibilityContext } from "views/AccessibilityContext";
import { WEEKDAYS_LONG, WEEKDAYS_SHORT } from "../../constants/weekdaysTypes";

export type DateRange = { from: Date | null | undefined; to: Date | null | undefined};

interface Props {
  value: DateRange;
  minDate?: Date | null;
  maxDate?: Date | null;
  errorMessage?: string;
  displayMonth?: Date;
  placeholder?: string;
  isValid?: boolean;
  onClick: (
    day: Date | null,
    modifiers: DayModifiers,
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined
  ) => void;
}

const DateInput = React.forwardRef<HTMLInputElement, PropsWithChildren<IInput>>((props, ref) => {
  return (
    <Input
      {...props}
      readOnly
      size="medium"
      ref={ref as any}
      name="DateRangePickerInput"
      className="day-picker-input date-picker-input"
      type="text"
      leftIcon={<CalendarIcon />}
      rightIcon={<DropdownIndicator />}
    />
  );
});

export const DateRangePicker: React.FC<Props> = ({
  value,
  errorMessage,
  displayMonth,
  minDate,
  maxDate,
  isValid = true,
  placeholder = "Odaberite period",
  onClick,
}) => {
  const dpiRef = React.useRef<any>(null);
  const { lowVision } = useContext(AccessibilityContext);

  const hasCompleteRange = value.from && value.to;

  const inputValue = hasCompleteRange
    ? `${format(value.from!, "dd.MM.yyyy.")} – ${format(value.to!, "dd.MM.yyyy.")}`
    : undefined;

  const today = () => {
    const t = new Date();
    if (minDate && t < minDate) {
      dpiRef.current?.hideDayPicker?.();
      return;
    }
    onClick?.(t, {} as DayModifiers, undefined);
    dpiRef.current?.hideDayPicker?.();
  };

  const onClear = () => {
    onClick?.(null, {} as DayModifiers, undefined);
    dpiRef.current?.hideDayPicker?.();
  };

  const availableDays = () => {
    const modifier: Partial<BeforeAfterModifier> = {};
    if (maxDate) modifier.after = maxDate;
    if (minDate) modifier.before = minDate;
    return modifier as BeforeAfterModifier;
  };

  const Overlay = React.forwardRef<HTMLDivElement, any>((props, ref) => {
    const { classNames, children, ...divProps } = props;
    return (
      <div
        ref={ref}
        className={`DayPickerInput-Overlay ${lowVision ? "low-vision" : classNames.overlayWrapper}`}
        {...divProps}
      >
        <div className={classNames.overlay}>
          {children}
          <div
            className="DayPicker-Footer"
            style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}
          >
            <Button type="button" className="DayPicker-TodayButton" onClick={today} size="small">
              Danas
            </Button>

            <Button isIcon type="button" size="small" className="DayPicker-TrashButton" onClick={onClear}>
              <TrashRedIcon style={{ width: 15, height: 15 }} />
            </Button>
          </div>
        </div>
      </div>
    );
  });

  return (
    <>
      <DayPickerInput
        ref={dpiRef}
        component={DateInput}
        placeholder={placeholder}
        inputProps={{ name: "DateRangePickerInput" }}
        formatDate={(date: Date) =>
          `${format(date, "dd.MM.yyyy.")} - ${WEEKDAYS_LONG[getDay(date)]}`
        }
        keepFocus={false}
        format="dd.MM.yyyy."
        value={inputValue}
        overlayComponent={Overlay}
        dayPickerProps={{
          navbarElement: <Navbar />,
          locale: "hr",
          weekdaysShort: WEEKDAYS_SHORT,
          onDayClick: (newDate, modifier, event) => {
            if (minDate && newDate < (minDate as Date)) return;
            if (value.from && isSameDay(newDate, value.from)) onClick(null, modifier, event);
            else onClick(newDate, modifier, event);
          },
          selectedDays:
            value.from && value.to
              ? [value.from, { from: value.from, to: value.to }]
              : value.from ?? undefined,
          firstDayOfWeek: 1,
          fromMonth: displayMonth,
          disabledDays: availableDays(),
        }}
      />

      {!isValid && <p className="error-message">{errorMessage}</p>}
    </>
  );
};
